import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { IGlobalState } from '../../model/IGlobalState';
import { startSetStock } from '../../actions/CurrentStock';
import AnimatedSaveButton from '../AnimatedSaveButton';
import { Announcement } from '../../model/Stock';
import SortableTable from '../table/SortableTable';
import api, { combinedTicker } from '../../api/api';
import { toSydneyDate, getBaseAddress } from '../../util/util';
import { FaExclamation, FaExternalLinkAlt, FaMailchimp } from 'react-icons/fa';
import moment from 'moment';
import AnnouncementMenu from './AnnouncementMenu';
import * as Roles from '../../model/Roles';
import styled from 'styled-components'
import AlertEx from '../AlertEx';
import { MergeAnnouncementsRequest } from '../../model/MergeAnnouncementsRequest';
import { EmailMarketingProviderType, EmailMarketingProviderList } from '../../model/EmailMarketingProviderType';

const Wrapper = styled.div`
.top-button-panel > *{
    margin-left: 1rem;
}
`
interface IProps {
    syncInProgress?: boolean;
    resyncAnnouncements?: () => Promise<boolean>;
    onAnnouncementEdit:(announcement:Announcement|null)=>void;
}


const DateColumn = 'Date';
const HeadlineColumn = 'Headline';
const ViewsColumn = 'Views';
const ActionsColumn = 'Actions';

const getHeaders = () =>  {
    return [
        DateColumn,
        HeadlineColumn,
        ViewsColumn,
        ActionsColumn,
    ];
}

export const getExternalLink = (announcement: Pick<Announcement, "localPath" | "remotePath">): string => {
    const localPath:string = announcement.localPath!;
    if (!(announcement.localPath||"").startsWith("/api/")){
        return announcement.remotePath!;
    }
    
    if(window.location.hostname !== 'localhost'){
        return `${getBaseAddress()}${localPath}`;
    }
    return api().getUrl(localPath);              
  }

  const getTitle = (announcement: Announcement): string => {
    let title = '';
    for (let meta of announcement.sendHistory || []) {
        const date = moment(meta.timestamp);
        //to Mailchimp
        title += `Sent by ${meta.emailAddress} on ${date.format('D MMM YYYY')} at ${date.format('h:mm A')}\n`
    }
    return title;
}

const AnnouncementTable: React.FC<IProps> = (props: IProps) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const announcements = useSelector((state: IGlobalState) => state.currentStock.announcements || []);
    const user = useSelector((state:IGlobalState) => state.user);
    const dispatch = useDispatch();
    // announcement.numComments only counts public responded comments (widget); unanswered
    // comments come from stock.commentEventGuids (server-side, role-gated)
    const commentEventGuids = stock.commentEventGuids || {};

    const onRemoveTag = async (announcement: Announcement, tag: string): Promise<void> => {
        if (!announcement.sendGuid) {
            AlertEx('This announcement has no sendGuid yet — cannot edit tags.');
            return;
        }
        const next = (announcement.tags || []).filter(t => t.toLowerCase() !== tag.toLowerCase());
        const result = await api().updateAnnouncementTags(combinedTicker(stock.ticker, stock.exchange), announcement.sendGuid, next);
        if (result.success) {
            dispatch(startSetStock(combinedTicker(stock.ticker, stock.exchange)) as any);
        } else {
            AlertEx(result.errorMessage || 'Failed to remove tag');
        }
    }

    const mergeAnnouncements = async () =>{
        
        const input = prompt("Enter the FROM Stock in the format TICKER:EXCHANGE");
      

        if (input !== null) {
            
            const [fromTicker, fromExchange] = input.split(":");
            const request: MergeAnnouncementsRequest = {
                toExchange: stock.exchange,
                toTicker: stock.ticker,
                fromExchange,
                fromTicker,
            }
            
            const result = await api().mergeAnnouncements(request);
            if (result.errorMessage) {
                AlertEx(`${result.errorMessage}`);
            } else {
                window.location.reload();
            }
        } 
        
       
        return true;
    }

    const cellFormatter = (columnName:string, cell:any, announcement:Announcement):React.ReactNode => {
        if(columnName===DateColumn){
            return toSydneyDate(cell)
        }
        else if(columnName===HeadlineColumn){
            const lastSend = announcement.sendHistory.length ? announcement.sendHistory[announcement.sendHistory.length-1] : undefined;
            // Maileroo's campaign-stats link is host-relative, so we always rebuild it from campaignId
            // (ignoring any stored campaignUrl, which may be absolute/stale). Other providers store an
            // absolute campaignUrl and are used as-is.
            const campaignUrl = !lastSend ? undefined
                : EmailMarketingProviderList[lastSend.providerType] === EmailMarketingProviderType.Maileroo
                    ? `/CampaignStats/${encodeURIComponent(lastSend.campaignId)}`
                    : lastSend.campaignUrl;
            const headlineChanged = announcement.headlineOriginal && announcement.headline !== announcement.headlineOriginal
            const comments = commentEventGuids[announcement.sendGuid || ''] || [];
            return <div>
                    <div className="announcement-row__headline">
                        {announcement.priceSensitive &&
                            <span className="announcement-row__price-sensitive" title="Price Sensitive"><FaExclamation /></span>
                        }
                        <span className={!!announcement.deletedOn ? " announcement-row__headline-text-deleted" : ""}>{announcement.headline}</span>

                        <a href={getExternalLink(announcement)}
                            rel="noopener noreferrer"
                            className="announcement-row__pdflink"
                            target="_blank">
                            <FaExternalLinkAlt size={12} color="grey" />
                        </a>
                        {announcement.deletedOn && <span className=' announcement-row__deleted'>Deleted on: {moment(announcement.deletedOn).tz('Australia/Sydney').format('D MMM YYYY h:mm A')}</span>}

                        { announcement.sent &&
                        <span className="announcement-row__sent" title={getTitle(announcement)}> <FaMailchimp />  </span>
                            }



                        <span className="announcement-row__links">
                            {!!campaignUrl && <a href={campaignUrl}>View Campaign</a>}
                            {comments.length > 0 &&
                             <Link to={`/AnnouncementCommentResponse/${comments[0]}`}>Comments ({comments.length})</Link>
                            }
                        </span>
                    </div>

                    {headlineChanged && <div className="announcement-row__headlineOriginal">Originally: {announcement.headlineOriginal}</div>}
                    {!!(announcement.tags && announcement.tags.length) && <div className="announcement-row__tags">
                        {announcement.tags.map(tag => (
                            <span key={tag} className="announcement-row__tag">
                                {tag}
                                <button
                                    type="button"
                                    className="announcement-row__tag-remove"
                                    title={`Remove tag ${tag}`}
                                    aria-label={`Remove tag ${tag}`}
                                    onClick={() => onRemoveTag(announcement, tag)}>×</button>
                            </span>
                        ))}
                    </div>}
                </div>
        }else if(columnName===ActionsColumn){
            return <AnnouncementMenu announcement={announcement}  onAnnouncementEdit={props.onAnnouncementEdit}/>
        }
        return null;
    }

    const data:any[][] = [];
        for(let announcement of announcements){
            const row = [
                announcement.date,
               announcement,
               announcement.hits||'',
               announcement,
               announcement
            ]
            data.push(row)
        }

    return <Wrapper>
            { !props.syncInProgress && <div className="row-reverse top-button-panel">
                <button onClick={()=>props.onAnnouncementEdit(null)}>Add Announcement</button>
                { 
                       user.roles.hasOwnProperty(Roles.MergeAnnouncements) && <AnimatedSaveButton onClick={mergeAnnouncements} startingText="Merge"  /> 
                       
                } 
               
                { 
                       user.roles.hasOwnProperty(Roles.ResyncAnnouncements) && props.resyncAnnouncements && 
                       <AnimatedSaveButton onClick={props.resyncAnnouncements} startingText="Resync"  /> 
                } 
        </div>}
            
                        

            <div className="horizontal">
                <p>{announcements.length} Announcements</p>
                <p style={{fontSize:'0.7rem', fontStyle:'italic'}}>Time is Sydney EDST</p>
            </div>

            <SortableTable headers={getHeaders()} data={data} cellFormatter={cellFormatter} sortDirection="desc"  />
        </Wrapper>    
}

export default AnnouncementTable;
